/**
 * SubscriptionOnboardingModal - Payment capture for invited users
 *
 * Flow:
 * 1. Fetches subscription plans (monthly/annual pricing)
 * 2. Displays invite benefit (e.g., "3 months free!")
 * 3. User selects plan
 * 4. User enters payment method via Stripe Elements
 * 5. Creates subscription with trial via API
 *
 */

import { SUPPORT_EMAIL } from '@/config/brandConfig'
import { useState, useEffect, useRef } from 'react'
import { Modal } from '../ui/Modal'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js'
import type { PaymentRequest } from '@stripe/stripe-js'
import { useTokenRefresh } from '../../hooks/useTokenRefresh'
import { clearAccessLevelCache } from '../../hooks/useAccessLevel'
import { LEGAL } from '@/config/legalConfig'
import { CheckoutAgreement, OfferChecklist, buildAgreement, AGREEMENT_REQUIRED } from '../legal/CheckoutAgreement'
import { PurchaseConfirmation, accessEnd, type PaidInfo } from '../legal/PurchaseConfirmation'
import { getPaymentMethods } from '../../api/paymentMethods'
import type { PaymentMethod } from '../../types/paymentMethod'

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!)

interface SubscriptionOnboardingModalProps {
  isOpen: boolean
  userId: string
  inviteToken: string | null
  onComplete: () => void
  onClose?: () => void // Called when user clicks X button to close modal
  onSkip?: () => void // Called when user clicks skip button
  allowSkip?: boolean // Whether to show skip button
}

interface SubscriptionPlan {
  id: string
  name: string
  description: string
  stripe_product_id: string
  stripe_price_id_monthly: string
  stripe_price_id_annual: string
  monthly_price: number
  annual_price: number
  features: string[]
  is_active: boolean
  sort_order: number
}

interface InviteDetails {
  id: string
  email: string
  benefit_type: string
  benefitLabel: string
  trialDays: number
  expires_at: string
}

export function SubscriptionOnboardingModal({
  isOpen,
  userId,
  inviteToken,
  onComplete,
  onClose
}: SubscriptionOnboardingModalProps) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [selectedPriceId, setSelectedPriceId] = useState('')
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [savedCards, setSavedCards] = useState<PaymentMethod[]>([]) // Saved payment methods
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  // After a successful payment: the confirmation screen, then onComplete
  const [paid, setPaid] = useState<PaidInfo | null>(null)

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('pending_invite_token')
    localStorage.removeItem('pending_invite_details')
    window.location.href = '/'
  }

  useEffect(() => {
    if (isOpen) {
      fetchPlans()
      fetchSavedCards()

      // CRITICAL FIX: Always fetch invite details if inviteToken is provided
      // Do NOT rely on localStorage - fetch fresh data from backend every time
      // This ensures incognito mode, page reloads, and multiple logins all work correctly
      if (inviteToken) {
        fetchInviteDetails(inviteToken)
      }
    }
  }, [isOpen, inviteToken])

  // Fetch saved payment methods
  const fetchSavedCards = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await getPaymentMethods(token)
      setSavedCards(response.payment_methods || [])
    } catch (err) {
      console.warn('[SubscriptionOnboarding] Could not load saved cards:', err)
      // Non-fatal - user can still enter new card
    }
  }

  const fetchPlans = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      // Pass auth token so backend can determine user's pricing cohort
      const response = await fetch(`${API_URL}/api/subscriptions/plans`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      })

      if (!response.ok) {
        throw new Error('Failed to fetch subscription plans')
      }

      const { plans: fetchedPlans } = await response.json()
      setPlans(fetchedPlans)

      // Auto-select first plan's monthly price
      if (fetchedPlans.length > 0) {
        setSelectedPriceId(fetchedPlans[0].stripe_price_id_monthly)
      }

      setLoading(false)
    } catch (err: any) {
      console.error('[SubscriptionOnboarding] Error fetching plans:', err)
      setError('Failed to load subscription plans')
      setLoading(false)
    }
  }

  const fetchInviteDetails = async (token: string) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/subscriptions/invites/${token}`)

      if (!response.ok) {
        console.error('[SubscriptionOnboarding] Failed to fetch invite details')
        return
      }

      const { invite } = await response.json()

      // Map benefit types to labels and trial days
      const benefitMap: Record<string, { label: string; days: number }> = {
        '3_months_free': { label: '3 months intro', days: 90 },
        '1_month_free': { label: '1 month intro', days: 30 },
        '10_days_free': { label: '10 days intro', days: 10 },
        '24_hours_free': { label: '24 hours intro', days: 1 },
        'full_subscription': { label: 'Full subscription', days: 0 }
      }

      const benefit = benefitMap[invite.benefit_type] || { label: 'Free trial', days: 0 }

      const details = {
        ...invite,
        benefitLabel: benefit.label,
        trialDays: benefit.days
      }

      setInviteDetails(details)
    } catch (err: any) {
      console.error('[SubscriptionOnboarding] Error fetching invite:', err)
      // Continue without invite details - will show as regular subscription
    }
  }

  const handlePlanSelect = (priceId: string) => {
    setSelectedPriceId(priceId)
  }

  if (!isOpen) return null

  if (paid) {
    return (
      <Modal isOpen={isOpen} onClose={() => {}} showCloseButton={false} maxWidth="max-w-lg">
        <PurchaseConfirmation info={paid} onContinue={onComplete} />
      </Modal>
    )
  }

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose || (() => {})} showCloseButton={!!onClose} maxWidth="max-w-lg">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Removing from waitlist...</p>
        </div>
      </Modal>
    )
  }

  if (error) {
    return (
      <>
        <Modal isOpen={isOpen} onClose={onClose || (() => {})} showCloseButton={!!onClose} maxWidth="max-w-lg">
          <div className="text-center py-8">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <p className="text-red-800 font-semibold mb-4">{error}</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setError('')
                  setLoading(true)
                  fetchPlans()
                }}
                className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </Modal>
      </>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={() => {}} showCloseButton={false} maxWidth="max-w-2xl">
      {/* Custom X button */}
      <button
        onClick={() => setShowLogoutConfirm(true)}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Close"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Logout Confirmation */}
      {showLogoutConfirm && (
        <div className="absolute inset-0 bg-pink-100 rounded-lg z-10 flex items-center justify-center">
          <div className="text-center p-6">
            <p className="text-lg font-semibold text-pink-900 mb-4">Would you like to log out?</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-pink-700 hover:text-pink-900 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Enable your access</h2>
          <p className="mt-2 text-sm text-gray-600">
            over 100,000 people on the waitlist
          </p>
        </div>

        {/* Approval Badge */}
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200/50 rounded-lg p-4">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl">🎉</span>
            <div className="text-center">
              <p className="font-bold text-pink-800">
                Congrats you have been approved to join Caryn World!
              </p>
            </div>
          </div>
        </div>

        {/* Plan Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Choose your plan
          </label>
          <div className="text-center mb-3"><OfferChecklist /></div>
          <div className="space-y-3">
            {plans.map((plan) => (
              <div key={plan.id} className="space-y-2">
                {/* Monthly Option */}
                <button
                  onClick={() => handlePlanSelect(plan.stripe_price_id_monthly)}
                  className={`
                    w-full text-left p-4 border rounded-lg transition-all
                    ${selectedPriceId === plan.stripe_price_id_monthly
                      ? 'border-pink-400/70 bg-pink-50 shadow-md'
                      : 'border-gray-200/60 hover:border-pink-300/70 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${selectedPriceId === plan.stripe_price_id_monthly
                          ? 'border-pink-500'
                          : 'border-gray-300'
                        }
                      `}>
                        {selectedPriceId === plan.stripe_price_id_monthly && (
                          <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          Full Private Access - Monthly
                        </p>
                        <p className="text-sm text-gray-600">
                          ${plan.monthly_price}/month • Billed monthly
                        </p>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Annual Option */}
                <button
                  onClick={() => handlePlanSelect(plan.stripe_price_id_annual)}
                  className={`
                    w-full text-left p-4 border rounded-lg transition-all relative
                    ${selectedPriceId === plan.stripe_price_id_annual
                      ? 'border-pink-400/70 bg-pink-50 shadow-md'
                      : 'border-gray-200/60 hover:border-pink-300/70 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${selectedPriceId === plan.stripe_price_id_annual
                          ? 'border-pink-500'
                          : 'border-gray-300'
                        }
                      `}>
                        {selectedPriceId === plan.stripe_price_id_annual && (
                          <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          Full Private Access - Annual
                        </p>
                        <p className="text-sm text-gray-600">
                          ${plan.annual_price}/year • Save ${(plan.monthly_price * 12 - plan.annual_price).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Stripe Payment Form */}
        <Elements stripe={stripePromise}>
          <PaymentForm
            userId={userId}
            selectedPriceId={selectedPriceId}
            selectedPriceAmount={(() => {
              const plan = plans.find(p =>
                p.stripe_price_id_monthly === selectedPriceId ||
                p.stripe_price_id_annual === selectedPriceId
              )
              // Check if selected price is annual
              const isAnnualPrice = plans.some(p => p.stripe_price_id_annual === selectedPriceId)

              if (!plan) {
                // Dynamic fallback based on monthly vs annual selection
                if (isAnnualPrice) {
                  return plans[0]?.annual_price || 700
                }
                return plans[0]?.monthly_price || 79
              }
              return plan.stripe_price_id_annual === selectedPriceId
                ? plan.annual_price
                : plan.monthly_price
            })()}
            isAnnual={plans.some(p => p.stripe_price_id_annual === selectedPriceId)}
            inviteToken={inviteToken}
            inviteDetails={inviteDetails}
            savedCards={savedCards}
            onPaid={setPaid}
          />
        </Elements>
      </div>

      {/* Help Button */}
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="fixed bottom-4 right-4 p-2 bg-pink-100 hover:bg-pink-200 rounded-full shadow-lg transition-colors"
        title="Contact Support"
      >
        <img src="/icons/support.png" alt="Support" style={{ width: '18px', height: '18px' }} />
      </a>
    </Modal>
  )
}

// Payment Form Sub-Component
interface PaymentFormProps {
  userId: string
  selectedPriceId: string
  selectedPriceAmount: number // Price in dollars (e.g., 29 for $29/month or 290 for $290/year)
  isAnnual: boolean
  inviteToken: string | null
  inviteDetails: InviteDetails | null
  savedCards: PaymentMethod[]
  onPaid: (info: PaidInfo) => void
}

function PaymentForm({
  selectedPriceId,
  selectedPriceAmount,
  isAnnual,
  inviteToken,
  inviteDetails: _inviteDetails,
  savedCards,
  onPaid
}: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [selectedSavedCard, setSelectedSavedCard] = useState<string | null>(null)
  const [agreed, setAgreed] = useState(false)
  // The Apple/Google Pay handler is registered once, so it reads these through refs
  const agreedRef = useRef(false)
  agreedRef.current = agreed
  const chargeLine = `Secure payment via Stripe. Your membership renews automatically at $${selectedPriceAmount} per ${isAnnual ? 'year' : 'month'} until you cancel in your Account. Cancel anytime.`
  const chargeLineRef = useRef(chargeLine)
  chargeLineRef.current = chargeLine
  const paidInfo = (endIso: string | null | undefined, card: { brand?: string | null; last4?: string | null } | null): PaidInfo => ({
    plan: isAnnual ? 'Full Private Access - Annual' : 'Full Private Access - Monthly',
    amount: `$${Number(selectedPriceAmount).toFixed(2)}`,
    paidAt: new Date(),
    renewal: isAnnual ? 'Renews automatically every year' : 'Renews automatically every month',
    endLabel: 'Next Renewal',
    endsAt: accessEnd(endIso, isAnnual ? 365 : 30),
    card,
    welcome: LEGAL.offer.welcome,
  })
  const paidInfoRef = useRef(paidInfo)
  paidInfoRef.current = paidInfo
  const { refreshToken } = useTokenRefresh()

  // Apple Pay / Google Pay state
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<'digital' | 'card'>('card')

  // Helper to get card brand icon
  const getCardBrandIcon = (brand: string) => {
    const brands: Record<string, string> = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳'
    }
    return brands[brand.toLowerCase()] || '💳'
  }

  // Initialize Payment Request (Apple Pay / Google Pay)
  useEffect(() => {
    if (!stripe || !selectedPriceId || !selectedPriceAmount) return

    // Use actual subscription price in cents
    const amountInCents = Math.round(selectedPriceAmount * 100)
    const label = isAnnual ? 'Caryn World - Annual' : 'Caryn World - Monthly'

    // If we already have a paymentRequest, update it instead of creating new one
    if (paymentRequest) {
      paymentRequest.update({
        total: {
          label,
          amount: amountInCents
        }
      })
      return // Don't create a new paymentRequest
    }

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label,
        amount: amountInCents
      },
      requestPayerName: true,
      requestPayerEmail: true
    })

    pr.canMakePayment().then((result) => {
      if (result) {
        const hasApplePay = result.applePay === true
        const hasGooglePay = result.googlePay === true

        if (hasApplePay || hasGooglePay) {
          setPaymentRequest(pr)

          // Check if mobile device
          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768

          if (isMobile) {
            // On mobile, default to Apple Pay / Google Pay
            setPaymentMethod('digital')
          } else {
            // On desktop, default to Card
            setPaymentMethod('card')
          }
        } else {
          setPaymentMethod('card')
        }
      } else {
        setPaymentMethod('card')
      }
    })

    // Handle payment method from Apple Pay / Google Pay
    pr.on('paymentmethod', async (e) => {
      if (!agreedRef.current) {
        e.complete('fail')
        setError(AGREEMENT_REQUIRED)
        return
      }
      try {
        setSubmitting(true)
        setError('')

        // Refresh token before payment
        try {
          await refreshToken()
        } catch (refreshErr) {
          console.warn('[PaymentForm] Token refresh failed (non-fatal):', refreshErr)
        }

        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
        const token = localStorage.getItem('auth_token')

        const response = await fetch(`${API_URL}/api/subscriptions/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            priceId: selectedPriceId,
            paymentMethodId: e.paymentMethod.id,
            inviteToken: inviteToken || undefined,
            agreement: buildAgreement(chargeLineRef.current)
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          e.complete('fail')
          throw new Error(errorData.error || 'Failed to create subscription')
        }

        e.complete('success')

        const walletData = await response.json()

        // Clear pending invite token
        localStorage.removeItem('pending_invite_token')
        localStorage.removeItem('pending_invite_details')

        // Wait briefly for webhook processing
        await new Promise(resolve => setTimeout(resolve, 2000))

        onPaid(paidInfoRef.current(walletData?.subscription?.current_period_end, e.paymentMethod.card ? { brand: e.paymentMethod.card.brand, last4: e.paymentMethod.card.last4 } : null))
      } catch (err: any) {
        console.error('[PaymentForm] Apple Pay error:', err)
        clearAccessLevelCache()
        setError(err.message || 'Payment failed')
        e.complete('fail')
      } finally {
        setSubmitting(false)
      }
    })

  }, [stripe, selectedPriceId, selectedPriceAmount, isAnnual, inviteToken, refreshToken, onPaid, paymentRequest])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      console.error('[PaymentForm] Stripe not loaded')
      return
    }

    if (!selectedPriceId) {
      setError('Please select a subscription plan')
      return
    }
    if (!agreed) {
      setError(AGREEMENT_REQUIRED)
      return
    }

    setSubmitting(true)
    setError('')

    try {
      // CRITICAL FIX: Refresh token before payment to prevent TOKEN_EXPIRED errors
      // This is non-blocking - if refresh fails, payment continues with existing token
      try {
        await refreshToken()
      } catch (refreshErr) {
        console.warn('[PaymentForm] Token refresh failed (non-fatal):', refreshErr)
        // Continue with payment - don't block on refresh failure
      }

      // Get payment method - either from saved card or create new one
      let paymentMethodId: string
      let usedCard: { brand?: string | null; last4?: string | null } | null = null
      if (selectedSavedCard) {
        const saved = savedCards.find(c => c.stripe_payment_method_id === selectedSavedCard)
        usedCard = saved ? { brand: saved.brand, last4: saved.last4 } : null
        // Use saved card
        paymentMethodId = selectedSavedCard
      } else {
        // Create payment method from card element
        const cardElement = elements.getElement(CardElement)

        if (!cardElement) {
          throw new Error('Card element not found')
        }

        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement
        })

        if (pmError || !paymentMethod) {
          throw new Error(pmError?.message || 'Failed to create payment method')
        }

        paymentMethodId = paymentMethod.id
        usedCard = paymentMethod.card ? { brand: paymentMethod.card.brand, last4: paymentMethod.card.last4 } : null
      }

      // Create subscription with automatic retry on token expiry
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

      // CRITICAL FIX: Retry logic for token expiration
      // Attempt subscription creation with automatic token refresh on expiry
      let attempt = 0
      const MAX_ATTEMPTS = 2
      let responseData

      while (attempt < MAX_ATTEMPTS) {
        attempt++

        // CRITICAL FIX: Fetch token RIGHT before request (not early in closure)
        // This ensures we always use the freshest token after async operations
        const token = localStorage.getItem('auth_token')

        const response = await fetch(`${API_URL}/api/subscriptions/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            priceId: selectedPriceId,
            paymentMethodId,
            inviteToken: inviteToken || undefined,
            agreement: buildAgreement(chargeLine)
          })
        })

        // Handle token expiry with automatic refresh and retry
        if (response.status === 401) {
          const errorData = await response.json()

          // If token expired and this is first attempt, refresh and retry
          if (errorData.code === 'TOKEN_EXPIRED' && attempt === 1) {
            try {
              const refreshed = await refreshToken()

              if (!refreshed) {
                throw new Error('Session expired. Please log in again.')
              }

              continue // Retry with new token
            } catch (refreshErr) {
              console.error('[PaymentForm] Token refresh failed:', refreshErr)
              throw new Error('Session expired. Please refresh the page and try again.')
            }
          }

          // Other 401 errors or second attempt failure
          throw new Error(errorData.error || 'Authentication failed. Please log in again.')
        }

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to create subscription')
        }

        // Success - break out of retry loop
        responseData = await response.json()
        break
      }

      if (!responseData) {
        throw new Error('Failed to create subscription after retries')
      }

      const { subscription } = responseData

      // CRITICAL FIX: If status is incomplete, try to sync with Stripe
      // This handles race conditions where payment succeeds but webhook hasn't fired yet
      if (subscription.status === 'incomplete') {
        try {
          // Fetch fresh token for sync request as well
          const syncResponse = await fetch(`${API_URL}/api/subscriptions/sync-status`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            }
          })

          if (syncResponse.ok) {
            const syncData = await syncResponse.json()

            // If sync shows active/trialing, we're done!
            if (syncData.subscription?.status === 'active' || syncData.subscription?.status === 'trialing') {
              // Clear pending invite token from localStorage
              localStorage.removeItem('pending_invite_token')

              // Payment done: show the confirmation, then onComplete
              onPaid(paidInfo(syncData.subscription?.current_period_end || subscription.current_period_end, usedCard))
              return
            }
            // no-op
          } else {
            console.warn('[PaymentForm] Sync request failed with status:', syncResponse.status)
          }
        } catch (syncErr) {
          console.warn('[PaymentForm] Status sync failed (non-fatal), falling back to grace period:', syncErr)
          // Fall through to existing wait logic
        }

        // Fall back to existing 3-second wait for webhook processing
        await new Promise(resolve => setTimeout(resolve, 3000))
      } else if (subscription.status === 'active' || subscription.status === 'trialing') {
        // If subscription is already active, we're good!
        // no-op
      } else {
        // Other status - still wait for webhooks
        await new Promise(resolve => setTimeout(resolve, 3000))
      }

      // Clear pending invite token and details from localStorage
      localStorage.removeItem('pending_invite_token')
      localStorage.removeItem('pending_invite_details')

      // Payment done: show the confirmation, then onComplete
      onPaid(paidInfo(subscription.current_period_end, usedCard))
    } catch (err: any) {
      console.error('[PaymentForm] Subscription creation failed:', err)
      console.error('[PaymentForm] Error stack:', err.stack)
      clearAccessLevelCache()
      setError(err.message || 'Failed to create subscription')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Payment Method Tabs - Only show if Apple Pay / Google Pay available */}
      {paymentRequest && (
        <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setPaymentMethod('digital')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
              paymentMethod === 'digital'
                ? 'bg-pink-500 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              Apple / Google Pay
            </span>
          </button>
          <button
            type="button"
            onClick={() => setPaymentMethod('card')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
              paymentMethod === 'card'
                ? 'bg-pink-500 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
              </svg>
              Card
            </span>
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Apple Pay / Google Pay Content */}
      {paymentMethod === 'digital' && paymentRequest ? (
        <div className="space-y-4">
          <div className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
            {!agreed ? (
              <p className="py-3 text-center text-sm text-gray-500">Tick the box below to pay with Apple Pay or Google Pay.</p>
            ) : (
            <PaymentRequestButtonElement
              options={{
                paymentRequest,
                style: {
                  paymentRequestButton: {
                    type: 'buy',
                    theme: 'dark',
                    height: '48px'
                  }
                }
              }}
            />
            )}
          </div>
          <p className="text-xs text-center text-gray-500">
            Fast, secure checkout with Apple Pay or Google Pay
          </p>
          {submitting && (
            <div className="flex items-center justify-center gap-2 text-pink-600">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-600"></div>
              <span>Processing...</span>
            </div>
          )}
        </div>
      ) : (
        /* Card Payment Form */
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Saved Payment Methods */}
          {savedCards.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Saved Cards
              </label>
              <div className="space-y-2">
                {savedCards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => setSelectedSavedCard(card.stripe_payment_method_id)}
                    className={`
                      w-full p-3 border rounded-lg text-left transition-all flex items-center gap-3
                      ${selectedSavedCard === card.stripe_payment_method_id
                        ? 'border-pink-400 bg-pink-50 shadow-sm'
                        : 'border-gray-200 hover:border-pink-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${selectedSavedCard === card.stripe_payment_method_id
                        ? 'border-pink-500'
                        : 'border-gray-300'
                      }
                    `}>
                      {selectedSavedCard === card.stripe_payment_method_id && (
                        <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                      )}
                    </div>
                    <span className="text-lg">{getCardBrandIcon(card.brand)}</span>
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 capitalize">{card.brand}</span>
                      <span className="text-gray-600"> **** {card.last4}</span>
                      <span className="text-gray-400 text-sm ml-2">
                        exp {card.exp_month}/{card.exp_year}
                      </span>
                    </div>
                    {card.is_default && (
                      <span className="text-xs text-pink-600 font-medium">Default</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* New Card Entry */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {savedCards.length > 0 ? 'Or enter a new card' : 'Payment Information'}
            </label>
            {savedCards.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedSavedCard(null)}
                className={`
                  w-full mb-2 p-3 border rounded-lg text-left transition-all flex items-center gap-3
                  ${selectedSavedCard === null
                    ? 'border-pink-400 bg-pink-50 shadow-sm'
                    : 'border-gray-200 hover:border-pink-300 hover:bg-gray-50'
                  }
                `}
              >
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center
                  ${selectedSavedCard === null ? 'border-pink-500' : 'border-gray-300'}
                `}>
                  {selectedSavedCard === null && (
                    <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                  )}
                </div>
                <span className="text-gray-700">Add new card</span>
              </button>
            )}
            {(selectedSavedCard === null || savedCards.length === 0) && (
              <div className="p-4 border-2 border-gray-200 rounded-lg focus-within:border-pink-500 transition-colors">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#374151',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        '::placeholder': {
                          color: '#9CA3AF'
                        }
                      },
                      invalid: {
                        color: '#EF4444'
                      }
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!stripe || submitting || !agreed}
            className="
              w-full py-3 px-6
              bg-pink-600
              text-white font-semibold rounded-lg
              hover:bg-pink-700
              disabled:bg-gray-300
              disabled:cursor-not-allowed
              transition-all duration-200
              shadow-md hover:shadow-lg
            "
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Accessing...
              </span>
            ) : (
              'Complete Access'
            )}
          </button>

        </form>
      )}

      {/* Agreement: required before paying (card or Apple/Google Pay), recorded with the payment */}
      <CheckoutAgreement checked={agreed} onChange={(v) => { setAgreed(v); if (v) setError('') }} chargeLine={chargeLine} />
    </div>
  )
}
